
import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChatMessage } from "@/components/notifications/chat/types";
import { setupChatRealtime } from "./useChatRealtime";
import { handleChatError } from "@/utils/chatErrorHandler";
import { v4 as uuidv4 } from "uuid";

export const useChatMessages = (notificationId: string | undefined) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Function to update read status - declare early so it can be used in useEffect
  const updateReadStatus = useCallback(async () => {
    if (!user?.id || !notificationId) return;
    
    try {
      const { error } = await supabase.rpc(
        'update_notification_chat_read_status',
        { 
          p_notification_id: notificationId,
          p_user_id: user.id
        }
      );
      
      if (error) {
        console.error("Error updating read status:", error);
      } else {
        console.log("Read status updated successfully");
        // Optimistically set unread count to 0 for this notification
        queryClient.setQueryData<Map<string, number>>(['all-notification-chat-counts'], (old) => {
          if (!old) return old;
          const updated = new Map(old);
          updated.set(notificationId!, 0);
          return updated;
        });
        queryClient.invalidateQueries({ queryKey: ['all-notification-chat-counts'] });
      }
    } catch (error) {
      console.error("Error updating read status:", error);
    }
  }, [user?.id, notificationId, queryClient]);

  useEffect(() => {
    if (!notificationId) return;
    
    const fetchMessages = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_chat')
        .select('*')
        .eq('notification_id', notificationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        handleChatError(error, 'load');
      } else {
        setMessages(data as ChatMessage[]);
      }
      setLoading(false);
    };

    fetchMessages();
    
    // Mark messages as read when opening notification
    if (user?.id) {
      setTimeout(() => updateReadStatus(), 100); // Small delay to ensure messages are loaded
    }
    
    // Set up realtime subscription using the utility function
    const cleanup = setupChatRealtime(
      notificationId,
      // onMessageInsert
      (payload) => {
        setMessages(prev => {
          const newMsg = payload.new as ChatMessage;
          if (prev.some(msg => msg.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        
        // Update read status for new messages from others
        if (user?.id && payload.new.user_id !== user.id) {
          setTimeout(() => updateReadStatus(), 500);
        }
      },
      // onMessageUpdate
      (payload) => {
        setMessages(prev => 
          prev.map(msg => msg.id === payload.new.id ? payload.new as ChatMessage : msg)
        );
      }
    );

    // Return cleanup function
    return cleanup;
  }, [notificationId, user?.id, updateReadStatus]);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(file.name);
    }
  }, []);

  const handleFileClear = useCallback(() => {
    setSelectedFile(null);
    setFilePreview(null);
  }, []);

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;
    if (!user || !notificationId) return;

    try {
      setUploading(true);

      // Get the current session user ID to ensure RLS policy compliance
      const { data: sessionData, error: sessionError } = await supabase
        .rpc('get_current_session_user_id');
      
      if (sessionError) {
        console.error("Error getting session user ID:", sessionError);
        handleChatError(sessionError, 'send');
        return;
      }
      
      // Use session user ID, fall back to auth user ID
      const userIdForInsert = sessionData || user.id;
      
      if (!userIdForInsert) {
        handleChatError(new Error("Geen geldige gebruikers ID gevonden"), 'send');
        return;
      }

      // Upload file if selected
      let attachmentUrl: string | null = null;
      if (selectedFile) {
        const ext = selectedFile.name.split('.').pop() || 'bin';
        const filePath = `${notificationId}/${uuidv4()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('message-attachments')
          .upload(filePath, selectedFile);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          toast.error("Bestand uploaden mislukt");
          return;
        }

        const { data: urlData } = supabase.storage
          .from('message-attachments')
          .getPublicUrl(uploadData.path);
        
        attachmentUrl = urlData.publicUrl;
      }
      
      const { data, error } = await supabase
        .from('notification_chat')
        .insert({
          notification_id: notificationId,
          user_id: userIdForInsert,
          user_name: user.name || 'Anonymous',
          message: newMessage.trim() || (selectedFile ? '' : ''),
          attachment_url: attachmentUrl
        })
        .select('*')
        .single();

      if (error) {
        console.error("Error sending message:", error);
        throw error;
      }
      
      setNewMessage("");
      setShowEmojiPicker(false);
      handleFileClear();
      
      // Update read status after sending a message
      setTimeout(() => updateReadStatus(), 100);
    } catch (error: any) {
      handleChatError(error, 'send');
    } finally {
      setUploading(false);
    }
  };

  const addEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const editMessage = async (messageId: string, text: string) => {
    if (!text.trim() || !user) return;

    try {
      const { error } = await supabase
        .from('notification_chat')
        .update({
          message: text.trim(),
          updated_at: new Date().toISOString(),
          is_edited: true
        })
        .eq('id', messageId);

      if (error) throw error;
      
      setEditingMessage(null);
      setEditText("");
    } catch (error: any) {
      handleChatError(error, 'edit');
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!user) return;
    
    if (!confirm("Weet je zeker dat je dit bericht wilt verwijderen?")) return;

    try {
      const { error } = await supabase
        .from('notification_chat')
        .update({
          is_deleted: true,
          message: "[Dit bericht is verwijderd]",
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) throw error;
    } catch (error: any) {
      handleChatError(error, 'delete');
    }
  };

  return {
    messages,
    loading,
    newMessage,
    setNewMessage,
    editingMessage,
    setEditingMessage,
    editText,
    setEditText,
    sendMessage,
    editMessage,
    deleteMessage,
    showEmojiPicker,
    setShowEmojiPicker,
    addEmoji,
    updateReadStatus,
    selectedFile,
    filePreview,
    handleFileSelect,
    handleFileClear,
    uploading
  };
};
